import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { AdminNav } from "@/components/AdminNav";
import { EngineModelSelect } from "@/components/EngineModelSelect";
import { extractDocumentText, sanitizeResume } from "@/lib/resumeSanitizer";
import {
  MAX_PAIRS,
  PAIR_STATUS_LABELS,
  batchIsActive,
  estimateCalls,
  groupFolderFiles,
  parseExpectedCsv,
  preparePair,
} from "@/lib/bulkAudit";

/*
 * Admin Panel — Bulk question audit (BULK_AUDIT_PLAN.md).
 *
 * Choose a folder of résumé / job description pairs, tick the pairs to run,
 * check them, start one batch and download one zip of question audits.
 * Each résumé goes through the upload page's privacy scan in this browser
 * before anything is sent; the server then runs the same stages as a manual
 * session (privacy review approved automatically), without credits or
 * sessions. Backed by /api/admin/bulk-audits, which exists only when the
 * server has BULK_AUDIT_ENABLED on (staging).
 */

const PAIR_ID_RE = /^[A-Za-z0-9][A-Za-z0-9 ._-]{0,79}$/;
const POLL_MS = 3000;

const STATUS_STYLES = {
  done: "bg-emerald-50 text-emerald-700 border-emerald-200",
  fit_warning: "bg-amber-50 text-amber-800 border-amber-200",
  privacy_blocked: "bg-rose-50 text-rose-700 border-rose-200",
  language_blocked: "bg-rose-50 text-rose-700 border-rose-200",
  error: "bg-rose-50 text-rose-700 border-rose-200",
  running: "bg-sky-50 text-sky-700 border-sky-200",
  waiting: "bg-slate-50 text-slate-600 border-slate-200",
  cancelled: "bg-slate-50 text-slate-500 border-slate-200",
};

function fmt(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

// When a test run started and finished, so runs can be told apart (2026-10-30).
function RunTimes({ run }) {
  if (!run?.started_at) return null;
  return (
    <div className="text-[color:var(--text-secondary)]" data-testid="bulk-run-times">
      Started {fmt(run.started_at)}{run.finished_at ? ` · finished ${fmt(run.finished_at)}` : " · still running"}
    </div>
  );
}

function Pill({ status, children }) {
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] ${STATUS_STYLES[status] || STATUS_STYLES.waiting}`}>
      {children || PAIR_STATUS_LABELS[status] || status}
    </span>
  );
}

function Button({ children, variant = "secondary", ...props }) {
  const cls = variant === "primary"
    ? "bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white border-[color:var(--brand)] disabled:opacity-50"
    : "border-[color:var(--border-stone)] hover:bg-[color:var(--bg-surface-hover)] disabled:opacity-50";
  return (
    <button type="button" className={`rounded-full border px-3 py-1 text-xs font-medium ${cls}`} {...props}>
      {children}
    </button>
  );
}

async function downloadZip(batchId, setDownloading) {
  setDownloading(true);
  try {
    const r = await api.get(`/admin/bulk-audits/${batchId}.zip`, { responseType: "blob" });
    const disposition = r.headers?.["content-disposition"] || "";
    const match = disposition.match(/filename="([^"]+)"/);
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = match ? match[1] : `bulk-audit_${batchId}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (ex) {
    toast.error(formatApiErrorDetail(ex));
  } finally {
    setDownloading(false);
  }
}

// Models the answer audit may try for the evidence check (backend ANSWER_AUDIT_CHECK_MODELS),
// each at its lowest reasoning effort.
const ANSWER_AUDIT_CHECK_MODELS = [
  ["", "Check model: current setting"],
  ["gpt-6-luna", "Check model: gpt-6-luna (fast)"],
  ["gpt-5.4-mini", "Check model: gpt-5.4-mini (fast)"],
];

export default function AdminBulkAudit() {
  const [enabled, setEnabled] = useState(null);
  const [batches, setBatches] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const refreshBatches = useCallback(async () => {
    try {
      const r = await api.get("/admin/bulk-audits");
      setBatches(r.data.batches || []);
      setEnabled(true);
    } catch (ex) {
      if (ex?.response?.status === 404) setEnabled(false);
      else toast.error(formatApiErrorDetail(ex));
    }
  }, []);

  useEffect(() => { refreshBatches(); }, [refreshBatches]);

  return (
    <div className="min-h-screen w-full bg-[color:var(--bg-base)]" data-testid="admin-bulk-audit">
      <AdminNav title="Bulk Question Audit" />
      <main className="max-w-6xl mx-auto p-6 space-y-6">
        {enabled === false ? (
          <div className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-6 text-sm" data-testid="bulk-audit-disabled">
            Bulk audit is switched off on this server. It runs on staging only, when the backend has
            <code className="mx-1">BULK_AUDIT_ENABLED=true</code>.
          </div>
        ) : enabled === null ? (
          <div className="text-sm text-[color:var(--text-secondary)]">Loading…</div>
        ) : (
          <>
            <NewBatch onStarted={(id) => { setActiveId(id); refreshBatches(); }} />
            {activeId && <Progress batchId={activeId} onChange={refreshBatches} />}
            <BatchList batches={batches} onOpen={setActiveId} onRefresh={refreshBatches} activeId={activeId} />
          </>
        )}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// New batch: folder → pairs → check → start
// ---------------------------------------------------------------------------

function NewBatch({ onStarted }) {
  const inputRef = useRef(null);
  const [folder, setFolder] = useState(null); // groupFolderFiles result
  const [rows, setRows] = useState([]);
  const [expected, setExpected] = useState({});
  const [filter, setFilter] = useState("");
  const [name, setName] = useState("");
  const [generateOnWarning, setGenerateOnWarning] = useState(false);
  const [newEngine, setNewEngine] = useState(""); // engine model for this batch's question generation
  const [checks, setChecks] = useState({}); // row key → preparePair result
  const [checking, setChecking] = useState(null); // {done, total}
  const [starting, setStarting] = useState(false);
  const nextKey = useRef(0);

  useEffect(() => {
    // React has no prop for folder selection; set the attributes directly.
    if (inputRef.current) {
      inputRef.current.setAttribute("webkitdirectory", "");
      inputRef.current.setAttribute("directory", "");
    }
  }, []);

  const resumeFiles = useMemo(() => (folder?.files || []).filter((f) => /\.(docx|pdf)$/i.test(f.name)), [folder]);
  const jdFiles = useMemo(() => (folder?.files || []).filter((f) => /\.(docx|pdf|txt)$/i.test(f.name)), [folder]);

  const onFolder = async (e) => {
    const grouped = groupFolderFiles(e.target.files);
    let exp = {};
    if (grouped.expectedPath) {
      try { exp = parseExpectedCsv(await grouped.filesByPath[grouped.expectedPath].text()); } catch { exp = {}; }
    }
    setFolder(grouped);
    setExpected(exp);
    setChecks({});
    setRows(grouped.pairs.map((p) => ({ ...p, key: nextKey.current++, selected: true })));
    const root = grouped.files[0]?.path.split("/")[0];
    setName((n) => n || (root ? `${root} ${new Date().toLocaleDateString()}` : ""));
  };

  const visible = rows.filter((r) => !filter || r.id.toLowerCase().includes(filter.toLowerCase()));
  const selected = rows.filter((r) => r.selected);

  const updateRow = (key, patch) => {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    if ("resumePath" in patch || "jdPath" in patch) {
      setChecks((c) => { const next = { ...c }; delete next[key]; return next; });
    }
  };
  const setVisibleSelected = (value) => {
    const keys = new Set(visible.map((r) => r.key));
    setRows((rs) => rs.map((r) => (keys.has(r.key) ? { ...r, selected: value } : r)));
  };
  const addPair = () => {
    const n = rows.length + 1;
    setRows((rs) => [...rs, {
      key: nextKey.current++, id: `pair_${n}`, selected: true,
      resumePath: resumeFiles[0]?.path || "", jdPath: jdFiles[0]?.path || "",
    }]);
  };

  const idProblems = useMemo(() => {
    const seen = new Map();
    const problems = {};
    for (const r of selected) {
      const lower = r.id.toLowerCase();
      if (!PAIR_ID_RE.test(r.id)) problems[r.key] = "Name: letters, digits, spaces, . _ - only";
      else if (seen.has(lower)) problems[r.key] = "Name used twice";
      seen.set(lower, true);
    }
    return problems;
  }, [selected]);

  const checkSelected = async () => {
    const todo = selected.filter((r) => !checks[r.key]);
    setChecking({ done: 0, total: todo.length });
    for (let i = 0; i < todo.length; i++) {
      const r = todo[i];
      const result = await preparePair(r, folder.filesByPath, { sanitize: sanitizeResume, extract: extractDocumentText });
      setChecks((c) => ({ ...c, [r.key]: result }));
      setChecking({ done: i + 1, total: todo.length });
    }
    setChecking(null);
  };

  const ready = selected.filter((r) => checks[r.key]?.ok && !idProblems[r.key]);
  const unchecked = selected.filter((r) => !checks[r.key]);
  const withProblems = selected.filter((r) => (checks[r.key] && !checks[r.key].ok) || idProblems[r.key]);
  const tooMany = ready.length > MAX_PAIRS;

  const start = async () => {
    if (!ready.length || tooMany || unchecked.length) return;
    const message = `Start "${name}" with ${ready.length} pair(s)? About ${estimateCalls(ready.length)} AI calls.` +
      (withProblems.length ? `\n${withProblems.length} pair(s) with problems will be left out.` : "");
    if (!window.confirm(message)) return;
    setStarting(true);
    try {
      const r = await api.post("/admin/bulk-audits", {
        name: name.trim(),
        generate_on_fit_warning: generateOnWarning,
        engine_model: newEngine,
        pairs: ready.map((row) => ({
          pair_id: row.id,
          resume_text: checks[row.key].resume_text,
          jd_text: checks[row.key].jd_text,
          expected: expected[row.id.toLowerCase()] || {},
        })),
        // Recorded in the zip's summary.csv and errors.csv, so it says what wasn't run and why.
        left_out: withProblems.map((row) => ({
          pair_id: row.id,
          problem: idProblems[row.key] || checks[row.key]?.problem || "",
        })),
      });
      toast.success(`Batch started: ${r.data.total} pairs`);
      setFolder(null);
      setRows([]);
      setChecks({}); // release the prepared texts
      if (inputRef.current) inputRef.current.value = "";
      onStarted(r.data.batch_id);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setStarting(false);
    }
  };

  return (
    <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 space-y-4" data-testid="bulk-new-batch">
      <div>
        <h2 className="text-sm font-semibold">New batch</h2>
        <p className="text-xs text-[color:var(--text-secondary)] mt-1">
          Choose a folder. Each sub-folder with one résumé and one job description is a pair, and loose files pair by
          a shared name or number (cv04 + jd04). An optional <code>expected.csv</code> (pair_id, expected_language,
          expected_fit) adds expected results to the summary. Résumés go through the privacy scan in this browser;
          the files never leave your computer.
        </p>
      </div>

      <input ref={inputRef} type="file" multiple onChange={onFolder} className="text-xs" data-testid="bulk-folder-input" />

      {folder && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <input
              value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter pairs"
              className="rounded-full border border-[color:var(--border-stone)] px-3 py-1" data-testid="bulk-filter"
            />
            <Button onClick={() => setVisibleSelected(true)} data-testid="bulk-select-all">Select all</Button>
            <Button onClick={() => setVisibleSelected(false)} data-testid="bulk-select-none">Select none</Button>
            <Button onClick={addPair} data-testid="bulk-add-pair">Add pair</Button>
            <span className="text-[color:var(--text-secondary)]">
              {selected.length} of {rows.length} pairs ticked
              {folder.expectedPath ? " · expected.csv loaded" : ""}
            </span>
          </div>

          <div className="overflow-x-auto rounded-xl border border-[color:var(--border-stone)]">
            <table className="w-full text-xs" data-testid="bulk-pairs-table">
              <thead className="bg-[color:var(--bg-surface-hover)] text-[color:var(--text-secondary)] uppercase tracking-widest">
                <tr>
                  <th className="px-3 py-2" />
                  <th className="text-left px-3 py-2">Pair</th>
                  <th className="text-left px-3 py-2">Résumé</th>
                  <th className="text-left px-3 py-2">Job description</th>
                  <th className="text-left px-3 py-2">Expected</th>
                  <th className="text-left px-3 py-2">Check</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const check = checks[r.key];
                  const exp = expected[r.id.toLowerCase()];
                  return (
                    <tr key={r.key} className="border-t border-[color:var(--border-stone)]" data-testid="bulk-pair-row">
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={r.selected} onChange={(e) => updateRow(r.key, { selected: e.target.checked })} aria-label={`Run ${r.id}`} />
                      </td>
                      <td className="px-3 py-2">
                        <input value={r.id} onChange={(e) => updateRow(r.key, { id: e.target.value })} className="w-44 rounded border border-[color:var(--border-stone)] px-2 py-0.5" aria-label="Pair name" />
                      </td>
                      <td className="px-3 py-2">
                        <FileSelect files={resumeFiles} value={r.resumePath} onChange={(v) => updateRow(r.key, { resumePath: v })} label="Résumé file" />
                      </td>
                      <td className="px-3 py-2">
                        <FileSelect files={jdFiles} value={r.jdPath} onChange={(v) => updateRow(r.key, { jdPath: v })} label="Job description file" />
                      </td>
                      <td className="px-3 py-2 text-[color:var(--text-secondary)]">
                        {exp ? [exp.language && `language: ${exp.language}`, exp.fit && `fit: ${exp.fit}`].filter(Boolean).join(" · ") || "—" : "—"}
                      </td>
                      <td className="px-3 py-2">
                        {idProblems[r.key] ? <Pill status="error">{idProblems[r.key]}</Pill>
                          : !check ? <span className="text-[color:var(--text-secondary)]">—</span>
                          : check.ok ? <Pill status="done">Ready</Pill>
                          : <Pill status="error">{check.problem}</Pill>}
                      </td>
                    </tr>
                  );
                })}
                {!visible.length && (
                  <tr><td colSpan={6} className="px-3 py-4 text-[color:var(--text-secondary)]">No pairs match.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {folder.unpaired.length > 0 && (
            <details className="text-xs" data-testid="bulk-unpaired">
              <summary className="cursor-pointer">{folder.unpaired.length} file(s) not paired</summary>
              <ul className="mt-1 list-disc pl-5 text-[color:var(--text-secondary)]">
                {folder.unpaired.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </details>
          )}

          <div className="flex flex-wrap items-center gap-3 text-xs">
            <input
              value={name} onChange={(e) => setName(e.target.value)} placeholder="Batch name" maxLength={120}
              className="rounded-full border border-[color:var(--border-stone)] px-3 py-1 w-64" data-testid="bulk-name"
            />
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={generateOnWarning} onChange={(e) => setGenerateOnWarning(e.target.checked)} data-testid="bulk-generate-on-warning" />
              Generate questions even when the fit check warns
            </label>
            <EngineModelSelect value={newEngine} onChange={setNewEngine} testId="bulk-new-engine" />
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs">
            <Button onClick={checkSelected} disabled={!selected.length || !!checking} data-testid="bulk-check">
              {checking ? `Checking ${checking.done} of ${checking.total}…` : "Check ticked pairs"}
            </Button>
            <Button
              variant="primary" onClick={start} data-testid="bulk-start"
              disabled={starting || !!checking || !ready.length || unchecked.length > 0 || tooMany || !name.trim()}
            >
              Start batch ({ready.length} pairs · about {estimateCalls(ready.length)} AI calls)
            </Button>
            {unchecked.length > 0 && <span className="text-[color:var(--text-secondary)]">{unchecked.length} ticked pair(s) not checked yet</span>}
            {withProblems.length > 0 && <span className="text-rose-700">{withProblems.length} pair(s) with problems will be left out</span>}
            {tooMany && <span className="text-rose-700">A batch can have at most {MAX_PAIRS} pairs</span>}
          </div>
        </>
      )}
    </section>
  );
}

function FileSelect({ files, value, onChange, label }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} className="max-w-[18rem] rounded border border-[color:var(--border-stone)] px-1 py-0.5">
      {!value && <option value="">—</option>}
      {files.map((f) => <option key={f.path} value={f.path}>{f.path.split("/").slice(1).join("/") || f.path}</option>)}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Progress of one batch
// ---------------------------------------------------------------------------

function Progress({ batchId, onChange }) {
  const [data, setData] = useState(null);
  const [checkModel, setCheckModel] = useState(""); // "" = the current FOLLOW_UP_CHECK_MODEL
  const [engine, setEngine] = useState(""); // engine model for this batch's test runs
  const [simEnglish, setSimEnglish] = useState(""); // "" = English level rotates question by question
  const [pollRun, setPollRun] = useState(0); // bumped to restart polling after stop / resume
  // One download at a time: a second click used to start a second full build.
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/bulk-audits/${batchId}`);
      setData(r.data);
      return r.data;
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
      return null;
    }
  }, [batchId]);

  useEffect(() => {
    let timer = null;
    let cancelled = false;
    const tick = async () => {
      const d = await load();
      if (cancelled) return;
      // Keep polling while the batch or its answer audit is running.
      if (d && (batchIsActive(d.batch.status) || d.batch.answer_audit?.status === "running"
        || d.batch.scoring_audit?.status === "running" || d.batch.simulation?.status === "running"
        || d.batch.english_probe?.status === "running")) timer = setTimeout(tick, POLL_MS);
      else onChange();
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [load, onChange, pollRun]);

  const act = async (path, body) => {
    try {
      await api.post(`/admin/bulk-audits/${batchId}/${path}`, body);
      onChange();
      setPollRun((n) => n + 1);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    }
  };

  if (!data) return null;
  const { batch, pairs } = data;
  const finished = pairs.filter((p) => !["waiting", "running"].includes(p.status)).length;
  const answerAudit = batch.answer_audit || null;
  const answerAuditRunning = answerAudit?.status === "running";
  const scoringAudit = batch.scoring_audit || null;
  const scoringAuditRunning = scoringAudit?.status === "running";
  const englishProbe = batch.english_probe || null;
  const englishProbeRunning = englishProbe?.status === "running";
  const simulation = batch.simulation || null;
  const simulationRunning = simulation?.status === "running";
  const simEnglishLabel = (v) => (v && v !== "rotate" && v !== "mixed" ? `all ${v}` : "rotating");
  return (
    <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 space-y-3" data-testid="bulk-progress">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">{batch.name}</h2>
          <div className="text-xs font-mono" data-testid="bulk-progress-batch-id">Batch ID: {batch.batch_id}</div>
          <div className="text-xs text-[color:var(--text-secondary)]">
            {batch.status} · {finished} of {batch.total} pairs finished · started {fmt(batch.started_at || batch.created_at)}
          </div>
        </div>
        <div className="flex gap-2">
          {batchIsActive(batch.status) && <Button onClick={() => act("stop")} data-testid="bulk-stop">Stop</Button>}
          {["stopped", "interrupted"].includes(batch.status) && <Button onClick={() => act("resume")} data-testid="bulk-resume">Resume</Button>}
          {batch.status === "done" && (
            <EngineModelSelect value={engine} onChange={setEngine} testId="bulk-engine" />
          )}
          {batch.status === "done" && !answerAuditRunning && (
            <select
              value={checkModel}
              onChange={(e) => setCheckModel(e.target.value)}
              aria-label="Evidence check model"
              title="Model the evidence check uses in this answer audit only"
              className="rounded border border-[color:var(--border-stone)] px-1 text-xs"
              data-testid="bulk-answer-audit-model"
            >
              {ANSWER_AUDIT_CHECK_MODELS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          )}
          {batch.status === "done" && !answerAuditRunning && (
            <Button
              onClick={() => {
                if (window.confirm("Write test answers for this batch's questions and check the follow-up decisions? About 8 AI calls per question.")) act("answer-audit", { check_model: checkModel, engine_model: engine });
              }}
              data-testid="bulk-answer-audit"
            >
              {answerAudit ? "Re-run answer audit" : "Run answer audit"}
            </Button>
          )}
          {batch.status === "done" && !scoringAuditRunning && (
            <Button
              onClick={() => {
                if (window.confirm("Score answers across content quality and English level for a sample of 8 questions, and check the scoring rules? About 10 scorings per question; a few minutes.")) act("scoring-audit", { engine_model: engine });
              }}
              data-testid="bulk-scoring-audit"
            >
              {scoringAudit ? "Re-run scoring audit" : "Run scoring audit"}
            </Button>
          )}
          {scoringAudit?.status === "done" && !scoringAuditRunning && !englishProbeRunning && (
            <Button
              onClick={() => {
                if (window.confirm("Re-score the scoring audit's strong-content answers in controlled versions (full, results cut, ordinary sentences cut, and against an unrelated question) with the English scorer, to see whether content moves the English score? About 5 English scorings per answer; a few minutes.")) act("english-probe", { engine_model: engine });
              }}
              data-testid="bulk-english-probe"
            >
              {englishProbe ? "Re-run English probe" : "Run English probe"}
            </Button>
          )}
          {batch.status === "done" && !simulationRunning && (
            <select
              value={simEnglish}
              onChange={(e) => setSimEnglish(e.target.value)}
              aria-label="Simulation English level"
              title="English level of the simulated answers in Simulate interviews only (the scoring audit always writes all three levels)"
              className="rounded border border-[color:var(--border-stone)] px-1 text-xs"
              data-testid="bulk-simulate-english"
            >
              <option value="">Simulation English: rotating</option>
              <option value="strong">Simulation English: all strong</option>
              <option value="intermediate">Simulation English: all intermediate</option>
              <option value="weak">Simulation English: all weak</option>
            </select>
          )}
          {batch.status === "done" && !simulationRunning && (
            <Button
              onClick={() => {
                const english = simEnglish ? `${simEnglish} English for every question` : "English level changing question by question";
                if (window.confirm(`Simulate a full interview for each of the first 10 finished pairs (mixed outcomes, ${english})? About 10-20 minutes. A re-run replaces the previous simulated interviews.`)) act("simulate", { english: simEnglish, engine_model: engine });
              }}
              data-testid="bulk-simulate"
            >
              {simulation ? "Re-run simulated interviews" : "Simulate interviews"}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => { if (!downloading) downloadZip(batchId, setDownloading); }}
            disabled={downloading}
            data-testid="bulk-download"
          >
            {downloading ? "Preparing zip…" : "Download zip"}
          </Button>
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-[color:var(--bg-surface-hover)] overflow-hidden">
        <div className="h-full bg-[color:var(--brand)]" style={{ width: `${batch.total ? (100 * finished) / batch.total : 0}%` }} />
      </div>
      {answerAudit && (
        <div className="rounded-xl border border-[color:var(--border-stone)] p-3 text-xs space-y-1" data-testid="bulk-answer-audit-status">
          <div className="font-medium">
            Answer audit: {answerAudit.status}
            {answerAudit.check_model ? ` · check model ${answerAudit.check_model}` : ""}
            {answerAuditRunning ? ` · ${answerAudit.done_questions || 0} of ${answerAudit.total_questions || 0} questions` : ""}
          </div>
          <RunTimes run={answerAudit} />
          {(answerAudit.summary || []).slice(0, 7).map((row) => (
            <div key={row.measure} className="text-[color:var(--text-secondary)]">
              {row.measure}: <strong className="text-[color:var(--text-primary)]">{row.value}</strong>
              {row.detail ? ` (${row.detail})` : ""}
            </div>
          ))}
          {answerAudit.status === "done" && <div className="text-[color:var(--text-secondary)]">Full results: answer_audit.csv in the zip.</div>}
        </div>
      )}
      {scoringAudit && (
        <div className="rounded-xl border border-[color:var(--border-stone)] p-3 text-xs space-y-1" data-testid="bulk-scoring-audit-status">
          <div className="font-medium">
            Scoring audit: {scoringAudit.status}
            {scoringAuditRunning ? ` · ${scoringAudit.done_questions || 0} of ${scoringAudit.total_questions || 0} questions` : ""}
          </div>
          <RunTimes run={scoringAudit} />
          {(scoringAudit.summary || []).filter((row) => row.measure !== "run").map((row) => (
            <div key={row.measure} className="text-[color:var(--text-secondary)]">
              {row.measure}: <strong className="text-[color:var(--text-primary)]">{row.value}</strong>
              {row.detail ? ` (${row.detail})` : ""}
            </div>
          ))}
          {scoringAudit.status === "done" && <div className="text-[color:var(--text-secondary)]">Full results: scoring_audit.csv and scoring_audit_checks.csv in the zip.</div>}
        </div>
      )}
      {englishProbe && (
        <div className="rounded-xl border border-[color:var(--border-stone)] p-3 text-xs space-y-1" data-testid="bulk-english-probe-status">
          <div className="font-medium">
            English probe: {englishProbe.status}
            {englishProbeRunning ? ` · ${englishProbe.done || 0} of ${englishProbe.total || 0} answers` : ""}
          </div>
          <RunTimes run={englishProbe} />
          {(englishProbe.summary || []).map((row) => (
            <div key={`${row.english}-${row.measure}`} className="text-[color:var(--text-secondary)]">
              {row.english} English · {row.measure}: <strong className="text-[color:var(--text-primary)]">{row.value}</strong>
              {row.detail ? ` (${row.detail})` : ""}
            </div>
          ))}
          {englishProbe.status === "done" && <div className="text-[color:var(--text-secondary)]">Full results: english_probe.csv in the zip.</div>}
        </div>
      )}
      {simulation && (
        <div className="rounded-xl border border-[color:var(--border-stone)] p-3 text-xs space-y-1" data-testid="bulk-simulation-status">
          <div className="font-medium">
            Simulated interviews: {simulation.status}
            {simulation.english ? ` · English ${simEnglishLabel(simulation.english)}` : ""}
            {simulation.engine_model ? ` · engine ${simulation.engine_model}` : ""}
            {simulationRunning ? ` · ${simulation.done_interviews || 0} of ${simulation.total_interviews || 0} interviews` : ""}
          </div>
          <RunTimes run={simulation} />
          {/* This run's own times: the batch header shows when the questions were generated. */}
          <div className="text-[color:var(--text-secondary)]" data-testid="bulk-simulation-times">
            Started {fmt(simulation.started_at)}
            {simulation.finished_at && !simulationRunning ? ` · finished ${fmt(simulation.finished_at)}` : ""}
          </div>
          {(simulation.summary || []).map((row) => (
            <div key={row.measure} className="text-[color:var(--text-secondary)]">
              {row.measure}: <strong className="text-[color:var(--text-primary)]">{row.value}</strong>
              {row.detail ? ` (${row.detail})` : ""}
            </div>
          ))}
          {simulation.status === "done" && <div className="text-[color:var(--text-secondary)]">Full results: simulation_answers.csv, simulation_interviews.csv and a text report per interview (simulation_reports) in the zip.</div>}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="bulk-progress-table">
          <thead className="text-[color:var(--text-secondary)] uppercase tracking-widest">
            <tr>
              <th className="text-left px-3 py-2">Pair</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Language</th>
              <th className="text-left px-3 py-2">Fit</th>
              <th className="text-left px-3 py-2">Detail</th>
            </tr>
          </thead>
          <tbody>
            {pairs.map((p) => (
              <tr key={p.pair_id} className="border-t border-[color:var(--border-stone)]">
                <td className="px-3 py-2">{p.pair_id}</td>
                <td className="px-3 py-2"><Pill status={p.status} /></td>
                <td className="px-3 py-2">{p.language_result || "—"}</td>
                <td className="px-3 py-2">{p.fit_result || "—"}</td>
                <td className="px-3 py-2 text-[color:var(--text-secondary)]">{p.detail || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Earlier batches
// ---------------------------------------------------------------------------

function BatchList({ batches, onOpen, onRefresh, activeId }) {
  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.name}" and all its results? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/bulk-audits/${b.batch_id}`);
      onRefresh();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    }
  };
  return (
    <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 space-y-3" data-testid="bulk-batches">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Batches</h2>
        <Button onClick={onRefresh}>Refresh</Button>
      </div>
      {!batches.length ? (
        <p className="text-xs text-[color:var(--text-secondary)]">No batches yet. Batches are deleted automatically after 30 days.</p>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-[color:var(--text-secondary)] uppercase tracking-widest">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Started</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Results</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => (
              <tr key={b.batch_id} className={`border-t border-[color:var(--border-stone)] ${b.batch_id === activeId ? "bg-[color:var(--bg-surface-hover)]" : ""}`}>
                <td className="px-3 py-2">
                  {b.name}
                  <div className="text-xs font-mono text-[color:var(--text-primary)]" data-testid={`bulk-batch-id-${b.batch_id}`}>Batch ID: {b.batch_id}</div>
                </td>
                <td className="px-3 py-2">{fmt(b.created_at)}</td>
                <td className="px-3 py-2">{b.status}</td>
                <td className="px-3 py-2 text-[color:var(--text-secondary)]">
                  {Object.entries(b.counts || {}).map(([k, v]) => `${PAIR_STATUS_LABELS[k] || k}: ${v}`).join(" · ")}
                </td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-2">
                    <Button onClick={() => onOpen(b.batch_id)}>View</Button>
                    <Button onClick={() => downloadZip(b.batch_id)}>Download</Button>
                    <Button onClick={() => remove(b)}>Delete</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
